import MsgTrans from '@components/common/i18n/message-trans'
import { AcademicCap } from '@components/common/icons/custom-icons'
import { FastlyImage } from '@components/common/image'
import CurriculumDownloadModal from '@components/home/curriculum-download-modal'
import CourseVideoModal from '@components/home/course-video-modal'
import CourseCard from '@components/locale-pages/home/course-card'
import {
  CourseInfoType,
  CurriculumVideoType,
} from '@components/locale-pages/home/courses'
import { Trans } from '@lingui/macro'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { BookOpen, Check, Clock, FileText, Play } from 'react-feather'
import Button from '@components/atoms/button'

const COMMON_IMAGE_PATH = '/images/home/courses/card/v4/'

const HOVER_BG = [
  'hover:bg-orange-100 hover:border-orange',
  'hover:bg-orange2-50 hover:border-orange2-400 hover:bg-opacity-75',
  'hover:bg-grey-200 hover:border-grey',
  'hover:bg-blue-50 hover:border-blue-300 hover:bg-opacity-50',
  'hover:bg-red-50 hover:border-red-200',
  'hover:bg-yellow-100 hover:bg-opacity-50 hover:border-yellow-200',
  'hover:bg-violet-50 hover:border-violet-100',
  'hover:bg-pink hover:border-pink',
]

type ResponsiveCourseCardProps = {
  course: CourseInfoType // Adjust type based on actual structure
}

const ResponsiveCourseCard = ({ course }: ResponsiveCourseCardProps) => {
  const router = useRouter()
  const registerHref = `/register/?course=${course.trialCourseQuery}&utm_medium=homepage-course-card`
  const getRandomHoverBg = () => {
    const randomIndex = Math.floor(Math.random() * HOVER_BG.length)
    return HOVER_BG[randomIndex]
  }
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [coursePDFCode, setCoursePDFCode] = useState<string | null>('cp')
  const [gradeOptions, setGradeOptions] = useState<number[]>([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ])
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false)
  const [isIframeVideoOpen, setIsIframeVideoOpen] = useState<boolean>(false)
  const [curriculumVideo, setCurriculumVideo] = useState<CurriculumVideoType[]>(
    []
  )
  const [courseTitle, setCourseTitle] = useState<React.ReactNode>(
    course?.titleCompactView
  )

  const handlePDFDownload = (courseCode: string, gradeOptions: any) => {
    setCoursePDFCode(courseCode)
    setIsModalOpen(true)
    setGradeOptions(gradeOptions)
  }

  const handleVideoModalOpen = (
    videos: CurriculumVideoType[],
    title: React.ReactNode
  ) => {
    setCurriculumVideo(videos)
    if (videos.length > 1) {
      setIsVideoModalOpen(true)
      setCourseTitle(title)
    } else {
      setIsIframeVideoOpen(true)
    }
  }

  return (
    <>
      <CurriculumDownloadModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        grades={gradeOptions}
        curriculumPDFPrefix={coursePDFCode.toLowerCase()}
      />
      {curriculumVideo.length > 0 && (
        <CourseVideoModal
          isModalOpen={isVideoModalOpen}
          setIsModalOpen={setIsVideoModalOpen}
          curriculumVideo={curriculumVideo}
          courseTitle={courseTitle}
          videoModalOpen={isIframeVideoOpen}
          setVideoModalOpen={setIsIframeVideoOpen}
        />
      )}
      <div
        role="link"
        tabIndex={0}
        onClick={() => router.push(registerHref)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') router.push(registerHref)
        }}
        className="py-2 w-full hidden sm:block md:hidden lg:block cursor-pointer"
      >
        <div
          className={`pb-0 shadow hover:shadow-lg transition-all duration-200 rounded-md bg-white ${getRandomHoverBg()} border border-grey-100 h-full flex gap-2`}
        >
          <div className="relative flex-shrink-0">
            {course.isAICourse && (
              <div className="relative top-0 left-0 z-10 mx-1">
                <div className="text-white font-700 text-lg p-2 absolute z-10">
                  AI
                </div>
                <img
                  src="/images/home/courses/bookmark.svg"
                  alt="bookmark"
                  className="absolute"
                />
              </div>
            )}
            <div className="rounded-md overflow-hidden h-full p-2 relative">
              <FastlyImage
                src={`${COMMON_IMAGE_PATH}${course.image}-potrate.jpg`}
                alt={course.altText}
                className="max-w-full h-full"
                width={214}
                height={364}
              />
              {course.curriculumVideos && (
                <button
                  className="absolute w-full h-full z-10 bottom-0 left-0 flex group focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleVideoModalOpen(
                      course?.curriculumVideos,
                      course?.titleCompactView as any
                    )
                  }}
                >
                  <span className="p-3 bg-white bg-opacity-75 m-auto rounded-full transition-all duration-200 group-hover:shadow-lg group-hover:bg-orange">
                    <Play size={16} fill="#000" />
                  </span>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col justify-between py-6 pr-2 space-y-3">
            <div>
              <h3 className="leading-tight font-600 duration-300 cursor-pointer hover:text-orange text-xl">
                {course.coursePageUrl ? (
                  <a
                    href={course.coursePageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {course.title}
                  </a>
                ) : (
                  <>{course.title}</>
                )}
              </h3>
              <div className="flex flex-wrap text-sm font-500 gap-2 mt-2">
                <div className="flex items-center bg-blue-50 bg-opacity-75 py-1 px-2 rounded-full">
                  <AcademicCap className="text-blue-200 w-4" />
                  <span style={{ marginLeft: '4px' }}>
                    <Trans>Grade</Trans> {course.grade}
                  </span>
                </div>
                <div className="flex items-center bg-yellow-100 bg-opacity-75 py-1 px-2 rounded-full">
                  <FileText className="text-yellow-200 w-3 mr-1" />
                  <span>
                    {course.activites}+ <Trans>Activities</Trans>
                  </span>
                </div>
                <div className="flex items-center bg-green-50 bg-opacity-75 py-1 px-2 rounded-full">
                  <BookOpen
                    className="text-green-100 w-3 mr-1"
                    style={{ marginLeft: '2px' }}
                  />
                  <span>
                    {course.lessons} <Trans>Lessons</Trans>
                  </span>
                </div>
                <div className="flex items-center bg-pink bg-opacity-75 py-1 px-2 rounded-full">
                  <Clock className="text-red-200 w-3 mr-1" />
                  <span>
                    {course.duration} <Trans>months</Trans>
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-600 mb-2">
                <Trans>Learning outcomes</Trans>
              </h4>
              <div className="space-y-1">
                {course.outcomes.map((outcome, index) => (
                  <div key={index} className="flex items-start">
                    <span
                      className="rounded-full bg-orange-200 mt-1"
                      style={{ padding: '2px' }}
                    >
                      <Check
                        height={10}
                        width={10}
                        strokeWidth={3}
                        className="text-orange"
                      />
                    </span>
                    <p className="ml-1 text-left">
                      <MsgTrans msg={outcome} />
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative w-full flex flex-wrap gap-3">
              <Link
                href={`/register/?course=${course.trialCourseQuery}&utm_medium=homepage-course-card`}
                prefetch={false}
              >
                <a className="w-full lg:w-auto">
                  <Button
                    variant="outline"
                    intent="primary"
                    size="md"
                    className="border-orange text-orange hover:bg-orange hover:text-white"
                  >
                    Try a free lesson
                  </Button>
                </a>
              </Link>
              {['cp', 'cc', 'cgm'].includes(course.courseCode) ? (
                <Button
                  variant="link"
                  className="w-full lg:w-auto"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handlePDFDownload(course.courseCode, course.gradeOptions)
                  }}
                >
                  <Trans>View curriculum</Trans>
                </Button>
              ) : (
                <a
                  href={course.curriculumPDFLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center justify-center align-middle w-full lg:w-auto"
                >
                  <Button variant="link">
                    <Trans>View curriculum</Trans>
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      <CourseCard
        course={course}
        onPDFDownload={handlePDFDownload}
        onVideoModalOpen={handleVideoModalOpen}
        isCoursePage
      />
    </>
  )
}

export default ResponsiveCourseCard
